import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import styles from '../stylesheets/Sidebar.module.css';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import Logo from '../../assets/focustodo-logo.svg';
import PlayIcon from '../../assets/play-icon.svg';
import PauseIcon from '../../assets/pause-icon.svg';
import ResetIcon from '../../assets/reset-icon.svg';
import TimerEndSound from '../../assets/timerEnd.wav';
import storage from './storage.js';
import NotesEditor from './NotesEditor.jsx';
import { getAppSurface, track, trackOncePerPage } from './analytics.js';

const SIDEBAR_VIEWS = {
  NOTES: 'notes',
  TIMER: 'timer',
};
const NOTE_SAVE_DEBOUNCE_MS = 140;
const CHEATSHEET_CLOSE_ANIMATION_MS = 180;
const CHEATSHEET_OPEN_STORAGE_KEY = 'markdownCheatsheetOpen';

const TIMER_MODES = {
  pomodoro: {
    title: 'Pomodoro',
    duration: 1500,
  },
  shortBreak: {
    title: 'Short break',
    duration: 300,
  },
  longBreak: {
    title: 'Long break',
    duration: 1200,
  },
};

const MARKDOWN_CHEATSHEET_ITEMS = [
  {
    id: 'heading',
    element: 'Main heading',
    keyText: '# Heading',
    type: 'heading',
    preview: 'Heading',
  },
  {
    id: 'subheading',
    element: 'Subheading',
    keyText: '## Subheading',
    type: 'subheading',
    preview: 'Subheading',
  },
  {
    id: 'bold',
    element: 'Bold text',
    keyText: '**Bold**',
    type: 'bold',
    preview: 'Bold',
  },
  {
    id: 'italic',
    element: 'Italic text',
    keyText: '*Italic*',
    type: 'italic',
    preview: 'Italic',
  },
  {
    id: 'unordered-list',
    element: 'Bullet list',
    keyText: '- List item',
    type: 'unorderedList',
    preview: 'List item',
  },
  {
    id: 'ordered-list',
    element: 'Numbered list',
    keyText: '1. List item',
    type: 'orderedList',
    preview: 'List item',
  },
  {
    id: 'quote',
    element: 'Quote',
    keyText: '> Quote',
    type: 'quote',
    preview: 'Quote',
  },
  {
    id: 'link',
    element: 'Link',
    keyText: '[Link](https://focustodo.io)',
    type: 'link',
    preview: 'Link',
  },
  {
    id: 'inline-code',
    element: 'Inline code',
    keyText: '`code`',
    type: 'inlineCode',
    preview: 'code',
  },
  {
    id: 'code-block',
    element: 'Code block',
    keyText: '``` function hello() {console.log("hi")}',
    type: 'codeBlock',
    preview: '',
  },
];

function formatTime(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const zeroPaddedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
  return `${minutes}:${zeroPaddedSeconds}`;
}

function getFormattedLogTime() {
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const formattedHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
  const meridiem = hours >= 12 ? 'PM' : 'AM';
  return `${formattedHours}:${formattedMinutes} ${meridiem}`;
}

function readStoredSidebarView() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return SIDEBAR_VIEWS.NOTES;
    const value = window.localStorage.getItem('sidebarView');
    if (value === SIDEBAR_VIEWS.NOTES || value === SIDEBAR_VIEWS.TIMER) {
      return value;
    }
  } catch (error) {
    // Ignore storage read errors and fall back to default view.
  }

  return SIDEBAR_VIEWS.NOTES;
}

function readStoredCheatsheetOpen() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return true;
    const value = window.localStorage.getItem(CHEATSHEET_OPEN_STORAGE_KEY);
    if (value === 'true') return true;
    if (value === 'false') return false;
  } catch (error) {
    // Ignore storage read errors and fall back to default state.
  }

  return true;
}

function persistCheatsheetOpen(nextValue) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(CHEATSHEET_OPEN_STORAGE_KEY, nextValue ? 'true' : 'false');
  } catch (error) {
    // Ignore storage write errors and keep runtime state.
  }
}

function renderMarkdownCheatsheetExample(item) {
  switch (item.type) {
    case 'heading':
      return <span className={styles.markdownCheatsheetExampleHeading}>{item.preview}</span>;
    case 'subheading':
      return <span className={styles.markdownCheatsheetExampleSubheading}>{item.preview}</span>;
    case 'bold':
      return <span className={styles.markdownCheatsheetExampleBold}>{item.preview}</span>;
    case 'italic':
      return <span className={styles.markdownCheatsheetExampleItalic}>{item.preview}</span>;
    case 'unorderedList':
      return <span>&bull; {item.preview}</span>;
    case 'orderedList':
      return <span>1. {item.preview}</span>;
    case 'quote':
      return <span className={styles.markdownCheatsheetExampleQuote}>{item.preview}</span>;
    case 'link':
      return (
        <a
          className={styles.markdownCheatsheetExampleLink}
          href="https://focustodo.io"
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.preview}
        </a>
      );
    case 'inlineCode':
      return <code className={styles.markdownCheatsheetExampleInlineCode}>{item.preview}</code>;
    case 'codeBlock':
      return (
        <code className={styles.markdownCheatsheetExampleCodeBlock}>
          <span className={styles.notesCodeTokenKeyword}>function</span>
          {' '}
          <span className={styles.notesCodeTokenFunction}>hello</span>
          <span className={styles.notesCodeTokenPunctuation}>()</span>
          {' '}
          <span className={styles.notesCodeTokenPunctuation}>{'{'}</span>
          {'console'}
          <span className={styles.notesCodeTokenPunctuation}>.</span>
          {'log'}
          <span className={styles.notesCodeTokenPunctuation}>(</span>
          <span className={styles.notesCodeTokenString}>{'"hi"'}</span>
          <span className={styles.notesCodeTokenPunctuation}>{')'}</span>
          <span className={styles.notesCodeTokenPunctuation}>{'}'}</span>
        </code>
      );
    default:
      return <span>{item.preview}</span>;
  }
}

export default function Sidebar({ highestId = 0 }) {
  const [timerMode, setTimerMode] = useState('pomodoro');
  const [remainingSeconds, setRemainingSeconds] = useState(TIMER_MODES.pomodoro.duration);
  const [isRunning, setIsRunning] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [sidebarView, setSidebarView] = useState(() => readStoredSidebarView());
  const [notesMarkdown, setNotesMarkdown] = useState('');
  const [notesReady, setNotesReady] = useState(false);
  const [hasEditedNotes, setHasEditedNotes] = useState(false);
  const [notesEditorKey, setNotesEditorKey] = useState(0);
  const [isCheatsheetOpen, setIsCheatsheetOpen] = useState(() => readStoredCheatsheetOpen());
  const [isCheatsheetClosing, setIsCheatsheetClosing] = useState(false);
  const [modeIndicator, setModeIndicator] = useState({
    left: 0,
    width: 0,
    ready: false,
  });

  const notesDebounceRef = useRef(null);
  const cheatsheetCloseTimeoutRef = useRef(null);
  const lastSavedNotesRef = useRef('');
  const latestNotesRef = useRef({ markdown: '', ready: false });
  const isMountedRef = useRef(true);
  const activeSaveRequestRef = useRef(0);
  const modeTabsRef = useRef(null);
  const notesTabRef = useRef(null);
  const timerTabRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioBufferRef = useRef(null);

  const timerConfig = TIMER_MODES[timerMode];

  useEffect(() => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return undefined;

    const context = new AudioContextClass();
    audioContextRef.current = context;

    fetch(TimerEndSound)
      .then((res) => res.arrayBuffer())
      .then((arrayBuffer) => context.decodeAudioData(arrayBuffer))
      .then((audioBuffer) => {
        audioBufferRef.current = audioBuffer;
      })
      .catch(() => {});

    return () => {
      context.close().catch(() => {});
      audioContextRef.current = null;
      audioBufferRef.current = null;
    };
  }, []);

  useEffect(() => {
    const today = new Date().toDateString();
    let isMounted = true;

    storage
      .loadState()
      .then((result) => {
        if (!isMounted) return;

        const storedActivity = result.state.activityLog || [];
        const lastUsedDate = result.state.lastUsedDate || '';

        if (lastUsedDate !== today) {
          setActivityLog([]);
          storage.saveActivityState({
            activityLog: [],
            lastUsedDate: today,
          });
        } else {
          setActivityLog(storedActivity);
        }

        const settings = result.state.settings || {};
        const loadedSidebarView = (settings.sidebarView === SIDEBAR_VIEWS.TIMER)
          ? SIDEBAR_VIEWS.TIMER
          : SIDEBAR_VIEWS.NOTES;

        const storedNotes = result.state.notes || {};
        const loadedMarkdown = (typeof storedNotes.markdown === 'string') ? storedNotes.markdown : '';
        setSidebarView(loadedSidebarView);
        setNotesMarkdown(loadedMarkdown);
        setNotesReady(true);
        setHasEditedNotes(false);
        setNotesEditorKey((current) => current + 1);
        lastSavedNotesRef.current = loadedMarkdown;
      })
      .catch(() => {
        if (!isMounted) return;

        setNotesReady(true);
        setHasEditedNotes(false);
        setNotesEditorKey((current) => current + 1);
      });

    return () => {
      isMounted = false;
      isMountedRef.current = false;
      if (cheatsheetCloseTimeoutRef.current) {
        window.clearTimeout(cheatsheetCloseTimeoutRef.current);
        cheatsheetCloseTimeoutRef.current = null;
      }
      if (notesDebounceRef.current) {
        window.clearTimeout(notesDebounceRef.current);
        notesDebounceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    latestNotesRef.current = {
      markdown: notesMarkdown,
      ready: notesReady,
    };
  }, [notesMarkdown, notesReady]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const inNotesView = sidebarView === SIDEBAR_VIEWS.NOTES;
    document.body.classList.toggle('sidebar-view-notes', inNotesView);

    return () => {
      document.body.classList.remove('sidebar-view-notes');
    };
  }, [sidebarView]);

  useEffect(() => {
    if (!isRunning) return undefined;

    const timerId = window.setInterval(() => {
      setRemainingSeconds((previous) => {
        if (previous <= 1) return 0;
        return previous - 1;
      });
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [isRunning]);

  const updateModeIndicator = useCallback(() => {
    const tabsElement = modeTabsRef.current;
    if (!tabsElement) return;

    const activeTab = sidebarView === SIDEBAR_VIEWS.NOTES ? notesTabRef.current : timerTabRef.current;
    if (!activeTab) return;

    const tabsRect = tabsElement.getBoundingClientRect();
    const activeRect = activeTab.getBoundingClientRect();
    const nextLeft = activeRect.left - tabsRect.left;
    const nextWidth = activeRect.width;

    setModeIndicator((previous) => {
      if (
        Math.abs(previous.left - nextLeft) < 0.25
        && Math.abs(previous.width - nextWidth) < 0.25
        && previous.ready
      ) {
        return previous;
      }

      return {
        left: nextLeft,
        width: nextWidth,
        ready: true,
      };
    });
  }, [sidebarView]);

  useLayoutEffect(() => {
    updateModeIndicator();
  }, [updateModeIndicator]);

  useEffect(() => {
    window.addEventListener('resize', updateModeIndicator);
    return () => {
      window.removeEventListener('resize', updateModeIndicator);
    };
  }, [updateModeIndicator]);

  const sendNotificationIfPossible = useCallback((notificationMessage) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notificationMessage);
    }
  }, []);

  const pickRandomEmoji = useCallback(() => {
    const emojis = ['🙌', '💪', '🔥', '🥇', '🎉', '💯', '😎', '🤩', '👊'];
    return emojis[Math.floor(Math.random() * emojis.length)];
  }, []);

  const handleTimerComplete = useCallback(() => {
    const timerMessage = `End of ${timerConfig.title}`;
    sendNotificationIfPossible(timerMessage);

    if (audioContextRef.current && audioBufferRef.current) {
      try {
        const source = audioContextRef.current.createBufferSource();
        source.buffer = audioBufferRef.current;
        source.connect(audioContextRef.current.destination);
        source.start(0);
      } catch (err) {
        const timerEndSound = new Audio(TimerEndSound);
        void timerEndSound.play().catch(() => {});
      }
    } else {
      const timerEndSound = new Audio(TimerEndSound);
      void timerEndSound.play().catch(() => {});
    }

    const formattedText =
      timerConfig.title === 'Pomodoro'
        ? `Completed work session ${pickRandomEmoji()}`
        : `End of ${timerConfig.title.toLowerCase()}`;

    const nextActivity = {
      time: getFormattedLogTime(),
      text: formattedText,
    };

    setActivityLog((current) => {
      const next = [nextActivity, ...current];
      storage.saveActivityState({
        activityLog: next,
        lastUsedDate: new Date().toDateString(),
      });
      return next;
    });
  }, [pickRandomEmoji, sendNotificationIfPossible, timerConfig.title]);

  useEffect(() => {
    document.title = isRunning
      ? (remainingSeconds > 0 ? formatTime(remainingSeconds) : '0:00')
      : 'focustodo.io';

    if (remainingSeconds === 0 && isRunning) {
      setIsRunning(false);
      handleTimerComplete();
    }
  }, [handleTimerComplete, isRunning, remainingSeconds]);

  useEffect(() => {
    return () => {
      document.title = 'focustodo.io';
    };
  }, []);

  const persistNotes = useCallback((markdown) => {
    if (!notesReady) return Promise.resolve();
    if (markdown === lastSavedNotesRef.current) return Promise.resolve();

    const requestId = activeSaveRequestRef.current + 1;
    activeSaveRequestRef.current = requestId;
    const updatedAt = new Date().toISOString();

    return storage.saveNotes({
      markdown,
      updatedAt,
    }).then(() => {
      if (activeSaveRequestRef.current !== requestId) return;
      lastSavedNotesRef.current = markdown;
    }).catch(() => {
      // Keep editing even if persistence fails for this write.
    });
  }, [notesReady]);

  useEffect(() => {
    if (!notesReady) return undefined;
    if (notesMarkdown === lastSavedNotesRef.current) return undefined;

    if (notesDebounceRef.current) {
      window.clearTimeout(notesDebounceRef.current);
      notesDebounceRef.current = null;
    }

    notesDebounceRef.current = window.setTimeout(() => {
      void persistNotes(notesMarkdown);
      notesDebounceRef.current = null;
    }, NOTE_SAVE_DEBOUNCE_MS);

    return () => {
      if (notesDebounceRef.current) {
        window.clearTimeout(notesDebounceRef.current);
        notesDebounceRef.current = null;
      }
    };
  }, [notesMarkdown, notesReady, persistNotes]);

  const flushNotesSave = useCallback((markdownOverride) => {
    if (notesDebounceRef.current) {
      window.clearTimeout(notesDebounceRef.current);
      notesDebounceRef.current = null;
    }

    const markdownToSave = (typeof markdownOverride === 'string')
      ? markdownOverride
      : latestNotesRef.current.markdown;
    return persistNotes(markdownToSave);
  }, [persistNotes]);

  useEffect(() => {
    const handlePageHide = () => {
      void flushNotesSave();
    };

    const handleBeforeUnload = () => {
      void flushNotesSave();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        void flushNotesSave();
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [flushNotesSave]);

  useEffect(() => {
    return () => {
      if (notesDebounceRef.current) {
        window.clearTimeout(notesDebounceRef.current);
        notesDebounceRef.current = null;
      }

      const latest = latestNotesRef.current;
      if (!latest.ready) return;
      if (latest.markdown === lastSavedNotesRef.current) return;

      void storage.saveNotes({
        markdown: latest.markdown,
        updatedAt: new Date().toISOString(),
      });
    };
  }, []);

  const percentComplete = useMemo(() => {
    if (timerConfig.duration === 0) return 0;
    return (remainingSeconds / timerConfig.duration) * 100;
  }, [remainingSeconds, timerConfig.duration]);

  const requestNotificationAccess = () => {
    if ('Notification' in window && Notification.permission === 'default') {
      void Notification.requestPermission();
    }
  };

  const startTimer = () => {
    if (remainingSeconds === 0) {
      setRemainingSeconds(timerConfig.duration);
    }

    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {});
    }

    requestNotificationAccess();
    trackOncePerPage('feature_use_timer', {
      app_surface: getAppSurface(),
      feature_name: 'timer',
    });
    track('timer_started', {
      app_surface: getAppSurface(),
      timer_mode: timerMode,
      source: 'play_button',
    });
    setIsRunning(true);
  };

  const pauseTimer = () => {
    setIsRunning(false);
  };

  const resetTimer = () => {
    setIsRunning(false);
    setRemainingSeconds(timerConfig.duration);
    document.title = 'focustodo.io';
  };

  const switchTimerMode = (mode) => {
    const nextConfig = TIMER_MODES[mode];
    if (!nextConfig) return;

    trackOncePerPage('feature_use_timer', {
      app_surface: getAppSurface(),
      feature_name: 'timer',
    });
    track('timer_started', {
      app_surface: getAppSurface(),
      timer_mode: mode,
      source: 'mode_switch',
    });
    setTimerMode(mode);
    setRemainingSeconds(nextConfig.duration);
    setIsRunning(true);
    requestNotificationAccess();
  };

  const clearActivity = () => {
    setActivityLog([]);
    storage.saveActivityState({
      activityLog: [],
      lastUsedDate: new Date().toDateString(),
    });
  };

  const switchSidebarView = (nextView) => {
    if (nextView !== SIDEBAR_VIEWS.NOTES && nextView !== SIDEBAR_VIEWS.TIMER) {
      return;
    }

    if (nextView === sidebarView) return;

    track('sidebar_view_changed', {
      app_surface: getAppSurface(),
      sidebar_view: nextView,
    });
    if (nextView === SIDEBAR_VIEWS.NOTES) {
      trackOncePerPage('feature_use_notes', {
        app_surface: getAppSurface(),
        feature_name: 'notes',
      });
    } else if (nextView === SIDEBAR_VIEWS.TIMER) {
      trackOncePerPage('feature_use_timer', {
        app_surface: getAppSurface(),
        feature_name: 'timer',
      });
    }

    setSidebarView(nextView);
    storage.saveSettings({ sidebarView: nextView });
    try {
      window.localStorage.setItem('sidebarView', nextView);
    } catch (error) {
      // Ignore storage write errors and keep runtime state.
    }

    if (nextView !== SIDEBAR_VIEWS.NOTES) {
      void flushNotesSave();
    }
  };

  const handleNotesMarkdownChange = useCallback((nextMarkdown) => {
    latestNotesRef.current = {
      ...latestNotesRef.current,
      markdown: nextMarkdown,
    };

    setNotesMarkdown((current) => {
      if (current === nextMarkdown) return current;
      trackOncePerPage('feature_use_notes', {
        app_surface: getAppSurface(),
        feature_name: 'notes',
      });
      return nextMarkdown;
    });
  }, []);

  const handleNotesBlur = useCallback(() => {
    const currentMarkdown = (typeof latestNotesRef.current.markdown === 'string')
      ? latestNotesRef.current.markdown
      : '';

    if (currentMarkdown.trim().length === 0) {
      if (currentMarkdown !== '') {
        setNotesMarkdown('');
        setNotesEditorKey((current) => current + 1);
        latestNotesRef.current = {
          ...latestNotesRef.current,
          markdown: '',
        };
      }
      void flushNotesSave('');
      return;
    }

    void flushNotesSave();
  }, [flushNotesSave]);

  const closeCheatsheet = useCallback(() => {
    if (!isCheatsheetOpen || isCheatsheetClosing) return;

    setIsCheatsheetClosing(true);
    persistCheatsheetOpen(false);
    if (cheatsheetCloseTimeoutRef.current) {
      window.clearTimeout(cheatsheetCloseTimeoutRef.current);
    }

    cheatsheetCloseTimeoutRef.current = window.setTimeout(() => {
      setIsCheatsheetOpen(false);
      setIsCheatsheetClosing(false);
      cheatsheetCloseTimeoutRef.current = null;
    }, CHEATSHEET_CLOSE_ANIMATION_MS);
  }, [isCheatsheetClosing, isCheatsheetOpen]);

  const openCheatsheet = useCallback(() => {
    if (cheatsheetCloseTimeoutRef.current) {
      window.clearTimeout(cheatsheetCloseTimeoutRef.current);
      cheatsheetCloseTimeoutRef.current = null;
    }
    setIsCheatsheetClosing(false);
    setIsCheatsheetOpen(true);
    persistCheatsheetOpen(true);
  }, []);

  const isCheatsheetCollapsed = !isCheatsheetOpen && !isCheatsheetClosing;
  const showNotesSavedStatus = notesReady && hasEditedNotes;
  const showNotesUtilityRow = isCheatsheetCollapsed || showNotesSavedStatus;

  return (
    <div className={styles.sidebarShell}>
      <div className={cx(styles.sidebar, styles.sidebarNotesView)}>
        <div className={cx(styles.sidebarContainer, styles.sidebarContainerNotes)}>
          <div className={styles.sidebarHeader}>
            <img className={cx(styles.focustodoLogo, styles.nodrag)} src={Logo} alt="focustodo.io" />

            <div className={styles.sidebarModeTabs} role="tablist" aria-label="Sidebar mode" ref={modeTabsRef}>
              <button
                ref={notesTabRef}
                type="button"
                role="tab"
                className={cx(styles.sidebarModeTab, sidebarView === SIDEBAR_VIEWS.NOTES && styles.sidebarModeTabActive)}
                aria-selected={sidebarView === SIDEBAR_VIEWS.NOTES}
                onClick={() => switchSidebarView(SIDEBAR_VIEWS.NOTES)}
              >
                notes
              </button>
              <button
                ref={timerTabRef}
                type="button"
                role="tab"
                className={cx(styles.sidebarModeTab, sidebarView === SIDEBAR_VIEWS.TIMER && styles.sidebarModeTabActive)}
                aria-selected={sidebarView === SIDEBAR_VIEWS.TIMER}
                onClick={() => switchSidebarView(SIDEBAR_VIEWS.TIMER)}
              >
                timer
              </button>

              <span
                className={cx(styles.sidebarModeIndicator, modeIndicator.ready && styles.sidebarModeIndicatorReady)}
                style={{
                  width: `${modeIndicator.width}px`,
                  transform: `translateX(${modeIndicator.left}px)`,
                }}
                aria-hidden="true"
              ></span>
            </div>
          </div>

          <div className={cx(styles.sidebarPanel, sidebarView !== SIDEBAR_VIEWS.TIMER && styles.sidebarPanelHidden)}>
            <h2 className={styles.sidebarHeading}>{timerConfig.title}</h2>
            <div className={styles.progressContainer}>
              <CircularProgressbarWithChildren
                counterClockwise={true}
                value={percentComplete}
                strokeWidth={2}
                styles={buildStyles({
                  pathColor: 'var(--progress-path)',
                  trailColor: 'var(--progress-trail)',
                })}
              >
                <div className={cx(styles.progressTime, 'animate__animated animate__fadeIn')}>
                  {formatTime(remainingSeconds)}
                </div>
                <div className={styles.progressIcons}>
                  {!isRunning ? (
                    <img
                      src={PlayIcon}
                      className={cx(styles.progressIcon, styles.nodrag)}
                      onClick={startTimer}
                      alt="Start timer"
                    />
                  ) : (
                    <img
                      src={PauseIcon}
                      className={cx(styles.progressIcon, styles.nodrag)}
                      onClick={pauseTimer}
                      alt="Pause timer"
                    />
                  )}
                  <img
                    src={ResetIcon}
                    className={cx(styles.progressIcon, styles.nodrag)}
                    onClick={resetTimer}
                    alt="Reset timer"
                  />
                </div>
              </CircularProgressbarWithChildren>
            </div>

            <div className={styles.pomodoroButtons}>
              <button className={styles.pomodoroButton} onClick={() => switchTimerMode('pomodoro')}>
                Pomodoro
              </button>
              <button className={styles.pomodoroButton} onClick={() => switchTimerMode('shortBreak')}>
                Short break
              </button>
              <button className={styles.pomodoroButton} onClick={() => switchTimerMode('longBreak')}>
                Long break
              </button>
            </div>

            <div className={styles.activityContainer}>
              <div className={styles.activityHeadingContainer}>
                <h2 className={cx(styles.sidebarHeading, styles.activityHeading)}>Today&apos;s Activity</h2>
                {activityLog.length > 0 && (
                  <button type="button" className={styles.activityAction} onClick={clearActivity}>
                    Clear all
                  </button>
                )}
              </div>

              {activityLog.length > 0 && (
                <div className={styles.activityList}>
                  {activityLog.map((activity, index) => (
                    <div className={styles.activityEntryContainer} key={`${activity.time}-${index}`}>
                      <div className={styles.activityEntryTime}>{activity.time}</div>
                      <div className={styles.activityEntryText}>{activity.text}</div>
                    </div>
                  ))}
                </div>
              )}

              {activityLog.length === 0 && highestId < 30 && (
                <div className={styles.noActivity}>
                  <div className={styles.noActivityLightbulb}>💡</div>
                  <span>Start a pomodoro to log your sessions and keep track of time.</span>
                </div>
              )}

              {activityLog.length === 0 && highestId >= 30 && (
                <div className={styles.noActivityUser}>No activity logged</div>
              )}
            </div>
          </div>

          <div
            className={cx(
              styles.sidebarPanel,
              styles.notesPanel,
              sidebarView !== SIDEBAR_VIEWS.NOTES && styles.sidebarPanelHidden,
            )}
          >
            {notesReady ? (
              <>
                <NotesEditor
                  key={notesEditorKey}
                  initialMarkdown={notesMarkdown}
                  onMarkdownChange={handleNotesMarkdownChange}
                  onUserInput={() => setHasEditedNotes(true)}
                  onBlur={handleNotesBlur}
                />
              </>
            ) : (
              <div className={styles.notesLoading}>Loading notes...</div>
            )}
          </div>
        </div>
      </div>

      {sidebarView === SIDEBAR_VIEWS.NOTES && notesReady && showNotesUtilityRow && (
        <div className={styles.notesBottomUtilityDock}>
          <div className={cx(styles.sidebarContainer, styles.sidebarContainerNotes)}>
            <div className={styles.notesBottomUtilityRow}>
              {isCheatsheetCollapsed && (
                <button
                  type="button"
                  className={styles.markdownCheatsheetReopenButton}
                  onClick={openCheatsheet}
                >
                  Markdown cheatsheet
                </button>
              )}

              {showNotesSavedStatus && (
                <div className={styles.notesBottomStatus}>Saved</div>
              )}
            </div>
          </div>
        </div>
      )}

      {sidebarView === SIDEBAR_VIEWS.NOTES && notesReady && (isCheatsheetOpen || isCheatsheetClosing) && (
        <div className={styles.markdownCheatsheetRow}>
          <div
            className={cx(
              styles.markdownCheatsheetCard,
              isCheatsheetClosing && styles.markdownCheatsheetCardClosing,
            )}
          >
            <button
              type="button"
              className={styles.markdownCheatsheetCloseButton}
              onClick={closeCheatsheet}
              aria-label="Close markdown cheatsheet"
            >
              ×
            </button>

            <div className={styles.markdownCheatsheetTitle}>Markdown cheatsheet</div>
            <div className={styles.markdownCheatsheetHeader}>
              <div>Element</div>
              <div>What to type</div>
              <div>What shows up</div>
            </div>
            <div className={styles.markdownCheatsheetList}>
              {MARKDOWN_CHEATSHEET_ITEMS.map((item) => (
                <div key={item.id} className={styles.markdownCheatsheetItem}>
                  <div className={styles.markdownCheatsheetElement}>{item.element}</div>
                  <code className={styles.markdownCheatsheetKey}>{item.keyText}</code>
                  <div className={styles.markdownCheatsheetExample}>
                    {renderMarkdownCheatsheetExample(item)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

Sidebar.propTypes = {
  highestId: PropTypes.number,
};
